import { useMutation, useQuery } from '@apollo/client/react';
import {
  ADD_INTL_ALL_REQUEST_NOTE,
  CANCEL_INTL_ALL_MCF_ORDER,
  CANCEL_INTL_ALL_QUOTE,
  CLEAR_INTL_ALL_SHOPIFY_CLAIM,
  SET_INTL_ALL_FREIGHT_OVERRIDE,
  APPROVE_INTL_ALL_FULFILMENT,
  REFRESH_INTL_ALL_REQUESTS,
  UPDATE_INTL_ALL_SHIPPING_ADDRESS,
  UNROUTE_INTL_ALL_REQUEST,
  REVOKE_INTL_ALL_FULFILMENT_APPROVAL,
  CONVERT_INTL_ALL_QUOTE_TO_INVOICE,
  CREATE_INTL_ALL_QUOTE,
  INTL_ALL_ELIGIBLE_MODELS,
  INTL_ALL_REQUEST_NOTES,
  INTL_ALL_REQUEST_PRICING,
  INTL_ALL_REQUESTS,
  PLACE_INTL_ALL_SHOPIFY_ORDER,
  RAISE_INTL_ALL_INVOICE,
  REFRESH_INTL_ALL_INVOICE,
  REFRESH_INTL_ALL_TRACKING,
  RELEASE_INTL_ALL_MCF_ORDER,
  ROUTE_INTL_ALL_REQUEST,
  SUBMIT_INTL_ALL_MCF_ORDER,
  SUBMIT_INTL_ALL_REQUEST,
  INTL_ALL_INVENTORY,
  INTL_ALL_MCF_STOCK,
} from '@staff-app/graphql-client';
import {
  INTL_DEVICE_MODELS,
  type ApproveIntlAllFulfilmentInput,
  type ClearIntlAllShopifyClaimInput,
  type ConvertIntlAllQuoteInput,
  type CreateIntlAllQuoteInput,
  type IntlAllEligibleModels,
  type IntlAllPricing,
  type IntlAllRefreshResult,
  type IntlAllRequest,
  type IntlAllRequestFilterInput,
  type IntlAllRequestNote,
  type IntlAllRequestPage,
  type IntlDeviceModel,
  type RaiseIntlAllInvoiceInput,
  type RouteIntlAllRequestInput,
  type SetIntlAllFreightOverrideInput,
  type UpdateIntlAllShippingAddressInput,
  type SubmitIntlAllRequestInput,
  type IntlAllInventoryLevel,
  type IntlAllMcfStock,
} from '@staff-app/shared-types';
import { useDebounced } from '@/features/order-devices/hooks/useOrderDeviceTasks';
import { useAuthStore } from '@/stores/auth-store';

/** Same three sizes as the International Requests table — two sibling tables should page alike. */
export const INTL_ALL_PAGE_SIZE = 50;
export const INTL_ALL_PAGE_SIZE_OPTIONS = [20, 50, 100] as const;

/**
 * Every mutation that changes the row refetches what is ON SCREEN — the list (which the drawer
 * reads its row from) and, when open, the pricing card — so stage, drawer and price agree. `active`
 * rather than a name list: the pricing query is mounted only for a routed request, and naming an
 * unmounted query is a warning per call.
 */
const REFETCH_ROW = {
  // Everything on screen re-reads after an action — the list, the drawer, the notes and the price
  // card — so the page shows the result without anyone pressing Refresh (Paul, 2026-10-28). It runs
  // in the background: the button is free again as soon as the action itself returns.
  refetchQueries: 'active' as const,
  awaitRefetchQueries: false,
};

export function useIntlAllRequests(filter: IntlAllRequestFilterInput, offset = 0, limit = INTL_ALL_PAGE_SIZE) {
  const debouncedSearch = useDebounced((filter.search ?? '').trim(), 300);
  return useQuery<{ intlAllRequests: IntlAllRequestPage }>(INTL_ALL_REQUESTS, {
    variables: {
      filter: {
        search: debouncedSearch || undefined,
        stage: filter.stage || undefined,
        route: filter.route || undefined,
        // Resolved SERVER-SIDE from the caller's token; sent only when true.
        mine: filter.mine ? true : undefined,
      },
      pagination: { offset, limit },
    },
    fetchPolicy: 'cache-and-network',
  });
}

export function useIntlAllRequestNotes(requestId: number | null) {
  return useQuery<{ intlAllRequestNotes: IntlAllRequestNote[] }>(INTL_ALL_REQUEST_NOTES, {
    variables: { requestId: requestId ?? 0 },
    skip: !requestId,
    fetchPolicy: 'cache-and-network',
  });
}

/**
 * The price as ROUTED. Skipped until the request is routed — freight is per shipment, so there is
 * nothing to price before then. Network-only: a cached figure might not be the one billed.
 */
export function useIntlAllRequestPricing(requestId: number | null) {
  return useQuery<{ intlAllRequestPricing: IntlAllPricing }, { requestId: number }>(
    INTL_ALL_REQUEST_PRICING,
    {
      variables: { requestId: requestId as number },
      skip: requestId == null,
      // The last price shows at once on reopening while a fresh one is computed (Paul, 2026-10-28);
      // quoting and invoicing re-price on the server, so a moment's stale figure bills nothing.
      fetchPolicy: 'cache-and-network',
      errorPolicy: 'all',
    },
  );
}

/**
 * Which models the Products List allows, per kind of route, in display order.
 *
 * `errorPolicy: 'all'` and an empty default: a refusal must read as "nothing ticked" with the
 * Products List named as the fix, not as a crashed form.
 */
export function useIntlAllEligibleModels(): IntlAllEligibleModels & { loading: boolean } {
  const { data, loading } = useQuery<{ intlAllEligibleModels: IntlAllEligibleModels }>(
    INTL_ALL_ELIGIBLE_MODELS,
    { fetchPolicy: 'cache-and-network', errorPolicy: 'all' },
  );
  const order = (list: readonly string[] | undefined): IntlDeviceModel[] =>
    INTL_DEVICE_MODELS.filter((m) => (list ?? []).includes(m));
  return {
    mcf: order(data?.intlAllEligibleModels?.mcf),
    shopify: order(data?.intlAllEligibleModels?.shopify),
    loading,
  };
}

export function useSubmitIntlAllRequest() {
  return useMutation<{ submitIntlAllRequest: IntlAllRequest }, { input: SubmitIntlAllRequestInput }>(
    SUBMIT_INTL_ALL_REQUEST,
    { refetchQueries: ['intlAllRequests'] },
  );
}

export function useRouteIntlAllRequest() {
  return useMutation<{ routeIntlAllRequest: IntlAllRequest }, { input: RouteIntlAllRequestInput }>(
    ROUTE_INTL_ALL_REQUEST,
    REFETCH_ROW,
  );
}

export function useCreateIntlAllQuote() {
  return useMutation<{ createIntlAllQuote: IntlAllRequest }, { input: CreateIntlAllQuoteInput }>(
    CREATE_INTL_ALL_QUOTE,
    REFETCH_ROW,
  );
}

export function useCancelIntlAllQuote() {
  return useMutation<{ cancelIntlAllQuote: IntlAllRequest }, { requestId: number }>(
    CANCEL_INTL_ALL_QUOTE,
    REFETCH_ROW,
  );
}

export function useConvertIntlAllQuote() {
  return useMutation<
    { convertIntlAllQuoteToInvoice: IntlAllRequest },
    { input: ConvertIntlAllQuoteInput }
  >(CONVERT_INTL_ALL_QUOTE_TO_INVOICE, REFETCH_ROW);
}

export function useRaiseIntlAllInvoice() {
  return useMutation<{ raiseIntlAllInvoice: IntlAllRequest }, { input: RaiseIntlAllInvoiceInput }>(
    RAISE_INTL_ALL_INVOICE,
    REFETCH_ROW,
  );
}

export function useRefreshIntlAllInvoice() {
  return useMutation<{ refreshIntlAllInvoice: IntlAllRequest }, { requestId: number }>(
    REFRESH_INTL_ALL_INVOICE,
    REFETCH_ROW,
  );
}

export function useSubmitIntlAllMcfOrder() {
  return useMutation<{ submitIntlAllMcfOrder: IntlAllRequest }, { requestId: number }>(
    SUBMIT_INTL_ALL_MCF_ORDER,
    REFETCH_ROW,
  );
}

export function useReleaseIntlAllMcfOrder() {
  return useMutation<{ releaseIntlAllMcfOrder: IntlAllRequest }, { requestId: number }>(
    RELEASE_INTL_ALL_MCF_ORDER,
    REFETCH_ROW,
  );
}

export function useCancelIntlAllMcfOrder() {
  return useMutation<{ cancelIntlAllMcfOrder: IntlAllRequest }, { requestId: number }>(
    CANCEL_INTL_ALL_MCF_ORDER,
    REFETCH_ROW,
  );
}

export function usePlaceIntlAllShopifyOrder() {
  return useMutation<
    { placeIntlAllShopifyOrder: IntlAllRequest },
    { requestId: number; route: string; allowShortfall?: boolean }
  >(PLACE_INTL_ALL_SHOPIFY_ORDER, REFETCH_ROW);
}

export function useClearIntlAllShopifyClaim() {
  return useMutation<
    { clearIntlAllShopifyClaim: IntlAllRequest },
    { input: ClearIntlAllShopifyClaimInput }
  >(CLEAR_INTL_ALL_SHOPIFY_CLAIM, REFETCH_ROW);
}

/** Refetches the pricing card too (REFETCH_ROW is `active`), so the new figure shows at once. */
export function useSetIntlAllFreightOverride() {
  return useMutation<
    { setIntlAllFreightOverride: IntlAllRequest },
    { input: SetIntlAllFreightOverrideInput }
  >(SET_INTL_ALL_FREIGHT_OVERRIDE, REFETCH_ROW);
}

export function useUnrouteIntlAllRequest() {
  return useMutation<{ unrouteIntlAllRequest: IntlAllRequest }, { requestId: number }>(
    UNROUTE_INTL_ALL_REQUEST,
    REFETCH_ROW,
  );
}

export function useUpdateIntlAllShippingAddress() {
  return useMutation<
    { updateIntlAllShippingAddress: IntlAllRequest },
    { input: UpdateIntlAllShippingAddressInput }
  >(UPDATE_INTL_ALL_SHIPPING_ADDRESS, REFETCH_ROW);
}

/** The page's Refresh — the caller refetches the list itself once this returns. */
export function useRefreshIntlAllRequests() {
  return useMutation<{ refreshIntlAllRequests: IntlAllRefreshResult }, { requestIds: number[] }>(
    REFRESH_INTL_ALL_REQUESTS,
  );
}

export function useApproveIntlAllFulfilment() {
  return useMutation<{ approveIntlAllFulfilment: IntlAllRequest }, { input: ApproveIntlAllFulfilmentInput }>(
    APPROVE_INTL_ALL_FULFILMENT,
    REFETCH_ROW,
  );
}

export function useRevokeIntlAllFulfilmentApproval() {
  return useMutation<{ revokeIntlAllFulfilmentApproval: IntlAllRequest }, { requestId: number }>(
    REVOKE_INTL_ALL_FULFILMENT_APPROVAL,
    REFETCH_ROW,
  );
}

export function useRefreshIntlAllTracking() {
  return useMutation<{ refreshIntlAllTracking: IntlAllRequest }, { requestId: number }>(
    REFRESH_INTL_ALL_TRACKING,
    REFETCH_ROW,
  );
}

export function useAddIntlAllRequestNote() {
  return useMutation<{ addIntlAllRequestNote: IntlAllRequestNote }, { requestId: number; text: string }>(
    ADD_INTL_ALL_REQUEST_NOTE,
    { refetchQueries: ['intlAllRequestNotes'] },
  );
}

// ---- Role tiers (UX only — the resolver's @RequireRoles is the enforcement) --------------------

function useHasRole(...wanted: string[]): boolean {
  const roles = useAuthStore((s) => s.adminUser?.roles);
  return Boolean(roles?.some((r) => wanted.includes(r)));
}

/** File, quote, convert, invoice — the requester's half. */
export const useCanFileAir = () => useHasRole('root', 'support');
/** Route, place orders — fulfilment's half. */
export const useCanFulfilAir = () => useHasRole('root', 'fulfillment');
/** Cancel a quote, add a note, clear a stuck Shopify create. */
export const useIsRootAir = () => useHasRole('root');

// ── Fulfillment inventory (ops sheet + Amazon) ──────────────────────────────────────────────

/** China / Houston on hand, from the ops inventory sheet. Pass the request being routed to leave it out of "routed, not ordered". */
export function useIntlAllInventory(requestId?: number, opts: { skip?: boolean } = {}) {
  return useQuery<{ intlAllInventory: IntlAllInventoryLevel[] }, { requestId?: number }>(INTL_ALL_INVENTORY, {
    variables: { requestId },
    skip: opts.skip,
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
  });
}

/** Amazon's live stock for the marketplace that would ship this request. */
export function useIntlAllMcfStock(requestId: number | null, opts: { skip?: boolean } = {}) {
  return useQuery<{ intlAllMcfStock: IntlAllMcfStock[] }, { requestId: number }>(INTL_ALL_MCF_STOCK, {
    variables: { requestId: requestId as number },
    skip: requestId == null || opts.skip,
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
  });
}
